import React, { useState, useRef, useEffect } from 'react';
import { ASSET_CDN_URL } from '../../Constants';
import { useLocation } from 'react-router-dom';
import useAuth from '../../hooks/AuthHook.jsx';
import { EditAccountButton, TextEditInput, EditInput, PhoneEditInput } from '../../locum/LocumManageAccountPage.jsx';
import StorePage from './StorePage.jsx';
import { USER_ROLES } from '../../Constants.js';
import { useOutsideClick } from '../../hooks/CommonHooks.jsx';
import './EditStorePage.scss';
import { NAVIGATION_OPTIONS } from '../BusinessConstants.js';
import { getProfile, postStoreContactDetails, postStoreOrganizationDetails, postStoreBankingDetails, postStoreAddressDetails, postStoreLocumOnboardingUpload, postStoreLocumOnboardingDelete, getAddressSuggestions } from '../../Api.js';

const DropdownArrow = `${ASSET_CDN_URL}/dropdown/black-dropdown-arrow.svg`;
const LocationDot = `${ASSET_CDN_URL}/icons/location-dot-icon.svg`;

const STRUCTURE_TYPES = {
  INDIVIDUAL_PROPRIETOR: { key: 'INDIVIDUAL_PROPRIETOR', text: 'Individual Proprietor' },
  PARTNERSHIP: { key: 'PARTNERSHIP', text: 'Partnership' },
  OTHER_PUBLIC_BODY: { key: 'OTHER_PUBLIC_BODY', text: 'Other Public Body' },
  COMPANY: { key: 'COMPANY', text: 'Company' },
  STATE_GOVERNMENT: { key: 'STATE_GOVERNMENT', text: 'State Government' },
  TERRITORY_GOVERNMENT: { key: 'TERRITORY_GOVERNMENT', text: 'Territory Government' },
  UNINCORPORATED_ASSOCIATION: { key: 'UNINCORPORATED_ASSOCIATION', text: 'Unincorporated Association' },
};

export const PREMISE_TYPES = {
  GENERAL_PRACTICE: { key: 'GENERAL_PRACTICE', text: 'Practice - General Practice' },
  OTHER_PRACTICE: { key: 'OTHER_PRACTICE', text: 'Practice - Other Private Practice' },
  PUBLIC_HOSPITAL: { key: 'PUBLIC_HOSPITAL', text: 'Hospital - Public' },
  PRIVATE_HOSPITAL: { key: 'PRIVATE_HOSPITAL', text: 'Hospital - Private' },
  RESIDENTIAL_CARE_FACILITY: { key: 'RESIDENTIAL_CARE_FACILITY', text: 'Residential Care Facility' },
  EDUCATIONAL_INSTITUTION: { key: 'EDUCATIONAL_INSTITUTION', text: 'Educational Institution' },
  OTHER: { key: 'OTHER', text: 'Other Community Healthcare Service' },
};

export const StoreDetailsDropdown = ({ selected, setSelected, options, disabled, placeholder, type }) => {
  const emptyText = disabled ? placeholder : 'Select an option...';
  const keys = Object.keys(options);
  const [open, setOpen] = useState(false);
  const toggleDropdown = () => setOpen(!open);
  const dropdownRef = useRef(null);
  useOutsideClick(dropdownRef, () => setOpen(false));
  const dropdownList = keys.map((key) => {
    const onSelection = () => {
      setSelected(key);
      toggleDropdown();
    }
    return (
      <li className={'dropdown-item'} key={`store-details-${key}`}>
        <button className={`dropdown-option`} onClick={onSelection}>
          {options[key].text}
        </button>
      </li>
    );
  });
  return (
    <section className={`dropdown-button ${open ? 'open' : ''} ${disabled ? 'disabled' : ''} ${type}`} ref={dropdownRef}>
      <button className={'data-view-button'} onClick={toggleDropdown} disabled={disabled}>
        {options[selected] ? options[selected].text : emptyText}
        { !disabled && 
          <img className={`dropdown-arrow ${open ? 'open' : ''}`} src={DropdownArrow} alt={'Dropdown arrow'} />
        }
      </button>
      {open &&
        <section className={'dropdown-list-container'}>
          <ul className={'dropdown-list'}>
            {dropdownList}
          </ul>
        </section>
      }
    </section>
  );
};

const DropdownEditInput = ({ label, children }) => {
  return (
    <EditInput label={label} type={'dropdown'}>
      {children}
    </EditInput>
  )
}

const StoreDetailSection = ({ title, type, save, edit, cancel, children, open, storeId }) => {
  const { profile } = useAuth();
  const canEdit = profile.role === USER_ROLES.BUSINESS && profile.stores && profile.stores.find(({ _id }) => _id === storeId);
  return (
    <section className={`details ${type}`}>
      <section className={'detail-title'}>
        <h2 className={'title'}>{title}</h2> 
        { canEdit && <EditAccountButton save={save} cancel={cancel} edit={edit} open={open} />}
      </section>
      {children}
    </section>
  );
}

const getMapboxContextText = (feature, typePrefix) => {
  if (feature.id && feature.id.includes(typePrefix)) {
    return feature.text || '';
  }
  const match = (feature.context || []).find(({ id }) => id && id.includes(typePrefix));
  return match ? match.text : '';
};

// AU place_name often has no comma between suburb and state: "Seymour Victoria 3660".
const localityFromPlaceName = (placeName, state, postcode) => {
  if (!placeName) {
    return '';
  }
  const parts = placeName.split(',').map((part) => part.trim()).filter(Boolean);
  if (parts.length < 2) {
    return '';
  }
  let suburb = parts[1];
  if (state) {
    suburb = suburb.replace(new RegExp(`\\b${state.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i'), ' ');
  }
  if (postcode) {
    suburb = suburb.replace(new RegExp(`\\b${postcode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i'), ' ');
  }
  return suburb.replace(/\s+/g, ' ').trim();
};

const AU_STATE_NAMES = {
  VIC: 'Victoria',
  NSW: 'New South Wales',
  QLD: 'Queensland',
  SA: 'South Australia',
  WA: 'Western Australia',
  TAS: 'Tasmania',
  NT: 'Northern Territory',
  ACT: 'Australian Capital Territory',
};
const AU_STATE_PATTERN = `${Object.keys(AU_STATE_NAMES).join('|')}|${Object.values(AU_STATE_NAMES).join('|')}`;

// Mapbox has no POI for many AU malls. A full pasted Google address still has
// suburb/state/postcode we can fill without a Mapbox hit.
const parsePastedAustralianAddress = (searchText) => {
  const text = `${searchText || ''}`.replace(/\s+/g, ' ').trim().replace(/,?\s*Australia$/i, '');
  const tail = text.match(new RegExp(`^(.*),\\s*([^,]+?)\\s+(${AU_STATE_PATTERN})\\s+(\\d{4})$`, 'i'));
  if (!tail) {
    return null;
  }
  let head = tail[1].trim();
  const locality = tail[2].trim();
  const stateRaw = tail[3];
  const postcode = tail[4];
  const state = AU_STATE_NAMES[stateRaw.toUpperCase()] || stateRaw;
  let shopNumber = '';
  const shopMatch = head.match(/^shop\s+([^,]+),?\s*(.*)$/i);
  if (shopMatch) {
    shopNumber = shopMatch[1].trim();
    head = shopMatch[2].trim();
  }
  let mall = '';
  const cleanStreet = (value) => value
    .replace(/\s*&\s*,\s*/g, ' & ')
    .replace(/\s*&\s*/g, ' & ')
    .replace(/\s+/g, ' ')
    .trim();
  let street = cleanStreet(head);
  const cnrMatch = head.match(/^(.*?)\s+cnr\.?\s+(.*)$/i);
  if (cnrMatch && cnrMatch[1].trim()) {
    mall = cnrMatch[1].trim();
    street = `Cnr ${cleanStreet(cnrMatch[2])}`;
  }
  if (!street || !locality || !postcode) {
    return null;
  }
  const parsed = {
    street,
    locality,
    city: '',
    state,
    postcode,
    country: 'Australia',
  };
  if (mall) {
    parsed.mall = mall;
  }
  if (shopNumber) {
    parsed.shopNumber = shopNumber;
  }
  return parsed;
};

const parseAddressSuggestion = (feature) => {
  const isPoi = (feature.place_type || []).includes('poi');
  const houseNumber = feature.address || '';
  const streetName = feature.text || '';
  const poiStreet = (feature.properties && feature.properties.address) || '';
  // POI text is the mall/store name — do not put it in Street.
  const street = isPoi
    ? poiStreet
    : (houseNumber ? `${houseNumber} ${streetName}` : streetName);
  const state = getMapboxContextText(feature, 'region') || '';
  const postcode = getMapboxContextText(feature, 'postcode') || '';
  // Metro suburbs are locality; regional towns are often place. Autocomplete
  // sometimes omits both from context and only puts the town in place_name.
  const locality = getMapboxContextText(feature, 'locality')
    || getMapboxContextText(feature, 'neighborhood')
    || getMapboxContextText(feature, 'place')
    || localityFromPlaceName(feature.place_name, state, postcode);
  const parsed = {
    street,
    locality,
    city: getMapboxContextText(feature, 'place') || '',
    state,
    postcode,
    country: getMapboxContextText(feature, 'country') || 'Australia',
  };
  if (isPoi) {
    parsed.mall = streetName;
  }
  return parsed;
};

const AddressSearch = ({ onSelect, searchText, setSearchText }) => {
  const [suggestions, setSuggestions] = useState([]);
  const searchRef = useRef(null);
  const skipNextFetch = useRef(false);
  const requestId = useRef(0);

  const closeSuggestions = () => {
    requestId.current += 1;
    setSuggestions([]);
  };

  useOutsideClick(searchRef, closeSuggestions);

  useEffect(() => {
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    if (searchText.length <= 3) {
      setSuggestions([]);
      return;
    }
    const id = requestId.current + 1;
    requestId.current = id;
    const pasted = parsePastedAustralianAddress(searchText);
    const pastedSuggestion = pasted
      ? [{ id: 'pasted-address', place_name: searchText, parsed: pasted }]
      : [];
    setSuggestions(pastedSuggestion);
    getAddressSuggestions(searchText.replace(/&/g, ' and '))
      .then((response) => {
        if (id !== requestId.current) {
          return;
        }
        const mapboxFeatures = response && response.features ? response.features : [];
        setSuggestions([...pastedSuggestion, ...mapboxFeatures]);
      })
      .catch(() => {
        if (id !== requestId.current) {
          return;
        }
        setSuggestions(pastedSuggestion);
      });
  }, [searchText]);

  const selectSuggestion = (feature) => {
    skipNextFetch.current = true;
    requestId.current += 1;
    onSelect(feature.parsed || parseAddressSuggestion(feature));
    setSearchText(feature.place_name || '');
    setSuggestions([]);
  };

  return (
    <section className={'edit-option text address-search'}>
      <section className={'edit-label'}>{'Search address'}</section>
      <div className={'address-search-field'} ref={searchRef}>
        <input
          type={'text'}
          className={'edit-text'}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={'Start typing a street or mall name...'}
        />
        {suggestions.length > 0 &&
          <ul className={'address-suggestions'}>
            {suggestions.map((feature) => (
              <li
                className={'address-suggestion'}
                key={feature.id}
                onClick={() => selectSuggestion(feature)}
              >
                <img className={'suggestion-icon'} src={LocationDot} alt={''} />
                <span className={'suggestion-text'}>{feature.place_name}</span>
              </li>
            ))}
          </ul>
        }
      </div>
    </section>
  );
};

const AddressDetails = ({ store }) => {
  const { profile, setProfile } = useAuth();
  const placeholder = profile.role === USER_ROLES.BUSINESS ? '' : 'Contact store for details';
  const canEdit = profile.role === USER_ROLES.BUSINESS && profile.stores && profile.stores.find(({ _id }) => _id === store._id);
  const initialAddress = store.address 
  ?
    {
      mall: store.address.mall ? store.address.mall : '',
      shopNumber: store.address.shopNumber ? store.address.shopNumber : '',
      street: store.address.street ? store.address.street : '',
      locality: store.address.suburb ? store.address.suburb : '',
      city: store.address.city ? store.address.city : '',
      state: store.address.state ? store.address.state : '',
      postcode: store.address.postcode ? store.address.postcode : '',
      country: store.address.country ? store.address.country : '',
    }
  : {
    mall: '',
    shopNumber: '',
    street: '',
    locality: '',
    city: '',
    postcode: '',
    state: '',
    country: '',
  }
  const [errorText, setErrorText] = useState(false);
  const [address, setAddress] = useState(initialAddress);
  const [searchText, setSearchText] = useState('');
  const saveAddress = (setLoading) => {
    setLoading(true);
    postStoreAddressDetails(store._id, { ...address, suburb: address.locality })
      .then(({ error }) => {
        if (error) {
          setErrorText(error);
          setLoading(false);
        }
        else {
          getProfile().then(({ profile }) => {
            setLoading(false);
            setProfile(profile);
          });
        }
      })
      .catch(({ error }) => {
        setErrorText(error);
        setLoading(false);
      }); 
  };
  const cancelAddress = () => {
    setAddress(initialAddress);
    setSearchText('');
  }
  const applyAddressSuggestion = (parsed) => {
    setErrorText('');
    setAddress((current) => ({ ...current, ...parsed }));
  };
  const changeMall = (e) => {
    setErrorText('');
    setAddress({ ...address, mall: e.target.value });
  };
  const changeShopNumber = (e) => {
    setErrorText('');
    setAddress({ ...address, shopNumber: e.target.value });
  };
  const changeStreet = (e) => {
    setErrorText('');
    setAddress({ ...address, street: e.target.value });
  };
  const changeLocality = (e) => {
    setErrorText('');
    setAddress({ ...address, locality: e.target.value });
  };
  const changeState = (e) => {
    setErrorText('');
    setAddress({ ...address, state: e.target.value });
  }
  const changePostcode = (e) => {
    setErrorText('');
    setAddress({ ...address, postcode: e.target.value });
  };
  const changeCountry = (e) => {
    setErrorText('');
    setAddress({ ...address, country: e.target.value });
  };
  const { mall, shopNumber, street, locality, postcode, state, country } = address;
  return (
    <StoreDetailSection
      title={'Address'}
      type={'address'}
      save={saveAddress}
      cancel={cancelAddress}
      storeId={store._id}
    >
      <span className={'error-text'}>{errorText}</span>
      <section className={'edit-options'}>
        {canEdit &&
          <AddressSearch
            onSelect={applyAddressSuggestion}
            searchText={searchText}
            setSearchText={setSearchText}
          />
        }
        <TextEditInput label={'Mall'} value={mall} onChange={changeMall} disabled={!canEdit} hideOnEmpty={profile.role === USER_ROLES.LOCUM} />
        <TextEditInput label={'Shop Number'} value={shopNumber} onChange={changeShopNumber} disabled={!canEdit} hideOnEmpty={profile.role === USER_ROLES.LOCUM} />
        <TextEditInput label={'Street'} value={street} onChange={changeStreet} disabled={!canEdit} placeholder={placeholder} />
        <TextEditInput label={'Locality'} value={locality} onChange={changeLocality} disabled={!canEdit} placeholder={placeholder} />
        <TextEditInput label={'State'} value={state} onChange={changeState} disabled={!canEdit} placeholder={placeholder} />
        <TextEditInput label={'Postcode'} value={postcode} onChange={changePostcode} disabled={!canEdit} placeholder={placeholder} />
        <TextEditInput label={'Country'} value={country} onChange={changeCountry} disabled={!canEdit} placeholder={placeholder} />
      </section>
    </StoreDetailSection>
  );
};

const ContactDetails = ({ store }) => {
  const { profile, setProfile } = useAuth();
  const placeholder = profile.role === USER_ROLES.BUSINESS ? '' : 'Contact store for details';
  const canEdit = profile.role === USER_ROLES.BUSINESS && profile.stores && profile.stores.find(({ _id }) => _id === store._id);
  console.log({ canEdit, profile, store });
  const initialContact = {
    telephoneNumber: store.contact && store.contact.telephoneNumber ? store.contact.telephoneNumber : '',
    mobileNumber: store.contact && store.contact.mobileNumber ? store.contact.mobileNumber : '',
    emailAddress: store.contact && store.contact.emailAddress ? store.contact.emailAddress : '',
    website: store.contact && store.contact.website ? store.contact.website : '',
  };
  const [name, setName] = useState(store.name);
  const [errorText, setErrorText] = useState(false);
  const [contact, setContact] = useState(initialContact);
  const saveContact = (setLoading) => {
    setLoading(true);
    const newMobileNumber = mobileNumber !== undefined && mobileNumber !== null ? `${contact.mobileNumber}` : ''
    postStoreContactDetails(store._id, { ...contact, mobileNumber: newMobileNumber }, name)
      .then(({ error }) => {
        if (error) {
          setErrorText(error);
          setLoading(false);
        }
        else {
          getProfile().then(({ profile }) => {
            setLoading(false);
            setContact({ ...contact, mobileNumber: newMobileNumber });
            setProfile(profile);
          });
        }
      })
      .catch(({ error }) => {
        setErrorText(error);
        setLoading(false);
      }); 
  };
  const cancelContact = () => {
    setContact(initialContact);
    setName(store.name);
  };
  const changeStoreName = (e) => {
    setErrorText('');
    setName(e.target.value);
  };
  const changeTelephoneNumber = (e) => {
    console.log({ e, contact });
    setErrorText('');
    setContact({ ...contact, telephoneNumber: e.target.value });
  };
  const changeMobileNumber = (phoneNumber) => {
    setErrorText('');
    setContact({ ...contact, mobileNumber: phoneNumber });
  };
  const changeEmailAddress = (e) => {
    setErrorText('');
    setContact({ ...contact, emailAddress: e.target.value });
  };
  const changeWebsite = (e) => {
    setErrorText('');
    setContact({ ...contact, website: e.target.value });
  };

  const onboardingDocument = store.locumOnboardingDocument ? store.locumOnboardingDocument : null;
  const hasOnboarding = onboardingDocument !== null;
  const uploadOnboarding = (e) => {
    const file = e.target.files[0];
    postStoreLocumOnboardingUpload(store._id, file)
      .then(() => getProfile().then(({ profile }) => {
        setProfile(profile);
        e.target.value = "";
      }))
      .catch((err) => console.error(err));
  };

  const deleteOnboarding = () => {
    postStoreLocumOnboardingDelete(store._id)
      .then(() => getProfile().then(({ profile }) => {
        setProfile(profile);
      }))
      .catch((err) => console.error(err));
  }

  const onboardingFileName = onboardingDocument ? onboardingDocument.fileName : 'No file chosen';
  const onboardingDocumentButton = canEdit
  ? (
    <section className={'upload-onboarding-container'}>
      <label for={'onboarding-upload'} className={'upload-onboarding'}>{'Upload Locum Onboarding Document'}</label>
      <input type={'file'} name={'onboarding-upload'} id={'onboarding-upload'} className={'onboarding-upload'} onChange={uploadOnboarding} />
      <span className={'delete-container'}>
        <span className={'file-name'}>{onboardingFileName}</span>
        { onboardingDocument && <button className={'delete'} onClick={deleteOnboarding}>{'×'}</button> }
      </span>
    </section>
  )
  : (
    <section className={'upload-onboarding-container'}>
      { hasOnboarding
          ? (
            <a className={`download-onboarding`} href={onboardingDocument.url} download={onboardingDocument.fileName} target={'_blank'} rel={'noreferrer noopener'}>
              {'Download Locum Onboarding Document'}
            </a>
          )
          : (
            <span className={'downloading-onboarding no-document'}>{'No Locum Onboarding Document Uploaded'}</span>
          )
      }
    </section>
  );

  const { telephoneNumber, mobileNumber, emailAddress, website } = contact;
  return (
    <StoreDetailSection
      title={'Contact'}
      type={'contact'}
      save={saveContact}
      cancel={cancelContact}
      storeId={store._id}
    >
      <span className={'error-text'}>{errorText}</span>
      <section className={'edit-options'}>
        <TextEditInput label={'Store name'} value={name} disabled={!canEdit} onChange={changeStoreName} />
        <TextEditInput label={'Telephone'} value={telephoneNumber} disabled={!canEdit} onChange={changeTelephoneNumber}/>
        <PhoneEditInput label={'Mobile'} value={mobileNumber} disabled={!canEdit} onChange={changeMobileNumber} />
        <TextEditInput label={'Nominated email for locums to contact'} disabled={!canEdit} value={emailAddress} onChange={changeEmailAddress} />
        <TextEditInput label={'Website'} value={website} disabled={!canEdit} onChange={changeWebsite} />
        {onboardingDocumentButton}
      </section>
    </StoreDetailSection>
  )
};

const OrganizationDetails = ({ store }) => {
  const { profile, setProfile } = useAuth();
  const placeholder = profile.role === USER_ROLES.BUSINESS ? ' ' : 'Contact store for details';
  const canEdit = profile.role === USER_ROLES.BUSINESS && profile.stores && profile.stores.find(({ _id }) => _id === store._id);
  const initialOrganization = store.organization 
    ? store.organization
    : {
      registeredBusinessName: null,
      abn: null,
      acn: null,
      tradingAs: null,
      structure: null,
      premises: null,
    }
  const [organization, setOrganization] = useState(initialOrganization);
  const [errorText, setErrorText] = useState(false);
  useEffect(() => {
    setOrganization(initialOrganization);
  }, [initialOrganization]);
  const saveOrganization = (setLoading) => {
    setLoading(true);
    postStoreOrganizationDetails(store._id, organization)
      .then(({ error }) => {
        if (error) {
          setErrorText(error);
          setLoading(false);
        }
        else {
          getProfile().then(({ profile }) => {
            setLoading(false);
            setProfile(profile);
          });
        }
      })
      .catch(({ error }) => {
        setErrorText(error);
        setLoading(false);
      }); 
  };
  const cancelOrganization = () => {
    setOrganization(initialOrganization);
  }
  const changeRegisteredBusinessName = (e) => {
    setErrorText('');
    setOrganization({ ...organization, registeredBusinessName: e.target.value });
  };
  const changeAbn = (e) => {
    setErrorText('');
    setOrganization({ ...organization, abn: e.target.value });
  };
  const changeAcn = (e) => {
    setErrorText('');
    setOrganization({ ...organization, acn: e.target.value });
  };
  const changeTradingAs = (e) => {
    setErrorText('');
    setOrganization({ ...organization, tradingAs: e.target.value });
  };
  const setStructure = (structure) => {
    setErrorText('');
    setOrganization({ ...organization, structure });
  };
  const setPremises = (premises) => {
    setErrorText('');
    setOrganization({ ...organization, premises });
  };
  const { registeredBusinessName, abn, acn, tradingAs, structure, premises } = organization;
  return (
    <StoreDetailSection
      title={'Organization'}
      type={'organization'}
      save={saveOrganization}
      cancel={cancelOrganization}
      storeId={store._id}
    >
    <span className={'error-text'}>{errorText}</span>
    <section className={'edit-options'}>
      <TextEditInput label={'Registered Business Name'} value={registeredBusinessName} onChange={changeRegisteredBusinessName} disabled={!canEdit} placeholder={placeholder} />
      <TextEditInput label={'ABN'} value={abn} onChange={changeAbn} disabled={!canEdit} placeholder={placeholder} />
      <TextEditInput label={'ACN'} value={acn} onChange={changeAcn} disabled={!canEdit} placeholder={placeholder} />
      <TextEditInput label={'Trading As'} value={tradingAs} onChange={changeTradingAs} disabled={!canEdit} placeholder={registeredBusinessName} />
      <DropdownEditInput label={'Business Type'}>
        <StoreDetailsDropdown type={'structure'} selected={structure} setSelected={setStructure} options={STRUCTURE_TYPES} disabled={!canEdit} placeholder={placeholder} />
      </DropdownEditInput>
      <DropdownEditInput label={'Premises Type'}>
        <StoreDetailsDropdown type={'premises'} selected={premises} setSelected={setPremises} options={PREMISE_TYPES} disabled={!canEdit} placeholder={placeholder} />
      </DropdownEditInput>
    </section>
    </StoreDetailSection>
  );
};

const BankingDetails = ({ store }) => {
  const { profile, setProfile } = useAuth();
  const placeholder = profile.role === USER_ROLES.BUSINESS ? '' : 'Contact store for details';
  const canEdit = profile.role === USER_ROLES.BUSINESS && profile.stores && profile.stores.find(({ _id }) => _id === store._id);
  const initialBanking = store.banking
    ? store.banking
    : {
      bsb: '',
      accountNumber: '',
      accountName: '',
      invoiceEmail: '',
    };
  const [banking, setBanking] = useState(initialBanking);
  useEffect(() => {
    setBanking(initialBanking);
  }, [initialBanking]);
  const [errorText, setErrorText] = useState(false);
  const saveBanking = (setLoading) => {
    setLoading(true);
    postStoreBankingDetails(store._id, banking)
      .then(({ error }) => {
        if (error) {
          setErrorText(error);
          setLoading(false);
        }
        else {
          getProfile().then(({ profile }) => {
            setLoading(false);
            setProfile(profile);
          });
        }
      })
      .catch(({ error }) => {
        setErrorText(error);
        setLoading(false);
      }); 
  };
  const cancelBanking = () => {
    setBanking(initialBanking)
  };
  const changeBsb = (e) => {
    setErrorText('');
    setBanking({ ...banking, bsb: e.target.value });
  };
  const changeAccountNumber = (e) => {
    setErrorText('');
    setBanking({ ...banking, accountNumber: e.target.value });
  };
  const changeAccountName = (e) => {
    setErrorText('');
    setBanking({ ...banking, accountName: e.target.value });
  };
  const changeInvoiceEmail = (e) => {
    setErrorText('');
    setBanking({ ...banking, invoiceEmail: e.target.value });
  }
  const { bsb, accountNumber, accountName, invoiceEmail = '' } = banking;
  return (
    <StoreDetailSection
      title={'Banking'}
      type={'banking'}
      save={saveBanking}
      cancel={cancelBanking}
      storeId={store._id}
    >
      <span className={'error-text'}>{errorText}</span>
      <section className={'edit-options'}>
        <TextEditInput label={'Branch Number (BSB)'} value={bsb} onChange={changeBsb} disabled={!canEdit} placeholder={placeholder} />
        <TextEditInput label={'Account Number'} value={accountNumber} onChange={changeAccountNumber} disabled={!canEdit} placeholder={placeholder} />
        <TextEditInput label={'Account Name'} value={accountName} onChange={changeAccountName} disabled={!canEdit} placeholder={placeholder} />
        <TextEditInput label={'Nominated Email To Receive Invoices'} value={invoiceEmail} onChange={changeInvoiceEmail} disabled={!canEdit} placeholder={placeholder} />
      </section>
    </StoreDetailSection>
  );
}


export const StoreDetails = ({ store = null, showEditButton = true }) => {
  const { profile } = useAuth();
  const { state } = useLocation();
  const [storeObject, setStoreObject] = useState(store);
  const storeId = state ? state.storeId : null;
  useEffect(() => {
    if (store === null && storeId) {
      setStoreObject(profile.stores.find((store) => store._id === storeId));
    }
  }, [store, storeId, profile.stores]);
  if (storeObject === null) {
    return null;
  }
  return (
    <section className={`store-details ${showEditButton ? '' : 'locum-view'}`}>
      <section className={'address-and-contact'}>
        <ContactDetails store={storeObject} showEditButton={showEditButton} />
        <AddressDetails store={storeObject} showEditButton={showEditButton} />
      </section>
      <section className={'organization-and-banking'}>
        <OrganizationDetails store={storeObject} showEditButton={showEditButton} />
        <BankingDetails store={storeObject} showEditButton={showEditButton} />
      </section>
    </section>
  );
}

const EditStorePage = () => {
  const { state } = useLocation();
  return (
    <StorePage mode={NAVIGATION_OPTIONS.STORE.mode}>
      <article className={'edit-store-page'}>
        <h1 className={'edit-store-title'}>{'Edit Store Details'}</h1>
        <p className={'edit-store-subtitle'}>
          {`Modify store name, address, phone number and other contact details below.`}
        </p>
        <p className={'edit-store-subtitle'}>
          {`This information is only shared with Locums confirmed for a shift to help setup their new provider numbers.`}
        </p>
        <StoreDetails />
      </article>
    </StorePage>
  );
};

export default EditStorePage;